import type { CGTemplatePairTableHeading } from '../types';
import type { Campaign } from '~/modules/campaign/types';

import {
  getCampaignTooltipBy,
  useAnalyticCampaignGroupContext,
} from '~/modules/campaign-analytics';
import { useCGTemplateMonitorCampaignPair } from '~/modules/campaign-group-template';
import { ICampaignMetricKey } from '~/modules/campaign-analytics/types';
import { CAMPAIGN_PRIORITY } from '~/constants/campaign';
import { useCampaignNavigate } from '~/modules/campaign';
import { useToggle } from '~/hooks/useToggle';
import { useCallback, useMemo } from 'react';

export const useHeadingCGTemplatePair = (campaign: Campaign) => {
  const { setTrue: onRenaming, setFalse: offRenaming, value: isRenaming } = useToggle(false);

  const goalSpecificHeadings = useGoalSpecificHeadings(campaign);

  const { onEditCampaign } = useCampaignNavigate();
  const { handleUpdateCampaignTemplate } = useCGTemplateMonitorCampaignPair();
  const { analyticMode } = useAnalyticCampaignGroupContext();

  const handleRenameCampaign = useCallback(
    async (name: string) => {
      if (!name) return;
      if (name === campaign.name) return;

      onRenaming();
      await handleUpdateCampaignTemplate(campaign, { name });
      offRenaming();
    },
    [campaign, handleUpdateCampaignTemplate, offRenaming, onRenaming],
  );

  return useMemo(() => {
    const baseHeadings: CGTemplatePairTableHeading[] = [
      {
        id: 'GTable-Cell--label',
        title: campaign.name ?? '',
        width: 'minmax(170px, 1fr)',
        text: { fontWeight: 'semibold', variant: 'bodyMd', truncate: true, isEnabledTooltip: true },
        linkAction: {
          monochrome: true,
          removeUnderline: true,
          onClick: () => onEditCampaign(campaign),
        },
        isStopPropagation: true,
        rename: {
          maxLength: 255,
          placeholder: 'Enter new name',
          loading: isRenaming,
          onAction: handleRenameCampaign,
        },
        padding: {
          paddingBlock: '200',
          paddingInline: '300',
        },
      },
      ...goalSpecificHeadings,
      {
        id: 'totalOrders',
        title: 'Orders',
        width: '170px',
        text: { fontWeight: 'semibold', variant: 'bodySm', tone: 'subdued' },
        tooltipCard: getCampaignTooltipBy(ICampaignMetricKey.ORDER, analyticMode),
        alignment: 'end',
      },
      {
        id: 'totalSessions',
        title: 'Sessions',
        width: '170px',
        text: { fontWeight: 'semibold', variant: 'bodySm', tone: 'subdued' },
        tooltipCard: getCampaignTooltipBy(ICampaignMetricKey.SESSION, analyticMode),
        alignment: 'end',
      },
      {
        id: 'action',
        title: '',
        width: '166px',
        alignment: 'end',
        padding: { paddingInlineEnd: '400' },
      },
    ];

    return baseHeadings.map((heading) => ({
      ...heading,
      padding: heading.padding || { paddingBlock: '400', paddingInline: '300' },
    }));
  }, [
    analyticMode,
    campaign,
    goalSpecificHeadings,
    handleRenameCampaign,
    isRenaming,
    onEditCampaign,
  ]);
};

const useGoalSpecificHeadings = (campaign: Campaign): CGTemplatePairTableHeading[] => {
  const { analyticMode } = useAnalyticCampaignGroupContext();

  if (!campaign.priority) return [];

  const conversionRateHeading: CGTemplatePairTableHeading = {
    id: 'conversionRate',
    title: 'Conversion',
    alignment: 'end',
    width: '170px',
    text: { fontWeight: 'semibold', variant: 'bodySm', tone: 'subdued' },
    tooltipCard: getCampaignTooltipBy(ICampaignMetricKey.CONVERSION_RATE, analyticMode),
    highlight: campaign.priority === CAMPAIGN_PRIORITY.CONVERSION,
  };
  const revenueRateHeading: CGTemplatePairTableHeading = {
    id: 'revenueRate',
    title: 'Revenue',
    alignment: 'end',
    width: '170px',
    text: { fontWeight: 'semibold', variant: 'bodySm', tone: 'subdued' },
    tooltipCard: getCampaignTooltipBy(ICampaignMetricKey.REVENUE, analyticMode),
    highlight: campaign.priority === CAMPAIGN_PRIORITY.REVENUE,
  };

  const headingsMap: Record<string, CGTemplatePairTableHeading[]> = {
    [CAMPAIGN_PRIORITY.CONVERSION]: [conversionRateHeading, revenueRateHeading],
    [CAMPAIGN_PRIORITY.REVENUE]: [revenueRateHeading, conversionRateHeading],
    [CAMPAIGN_PRIORITY.PROFIT]: [conversionRateHeading, revenueRateHeading],
  };

  return headingsMap[campaign.priority] || headingsMap[CAMPAIGN_PRIORITY.CONVERSION];
};
